import {PlatformType, SocialLink} from '@/types/social_links';
import {
    IconBrandInstagram,
    IconBrandFacebook,
    IconBrandSoundcloud,
    IconBrandSpotify,
    IconBrandTwitter,
    IconLink,
} from '@tabler/icons-react';
import React from 'react';

export function parseSocialLinks(value: string | null): SocialLink[] {
    if (!value) return [];

    try {
        const parsed = JSON.parse(value);
        return parsed?.links || [];
    } catch {
        return [];
    }
}

export function stringifySocialLinks(links: SocialLink[]): string {
    return JSON.stringify({links});
}

export function extractUsername(url: string, platform: PlatformType): string {
    if (!url) return '';

    try {
        const urlObj = new URL(url);
        const pathname = urlObj.pathname;

        if (platform === 'instagram') {
            const match = pathname.match(/^\/([^/]+)/);
            return match ? `@${match[1]}` : url;
        }

        if (platform === 'bandcamp') {
            const hostname = urlObj.hostname;
            if (hostname.endsWith('.bandcamp.com')) {
                return hostname.replace('.bandcamp.com', '');
            }
            if (hostname === 'bandcamp.com') {
                const match = pathname.match(/^\/([^/]+)/);
                return match ? match[1] : url;
            }
            return hostname;
        }

        if (platform === 'facebook') {
            const match = pathname.match(/^\/([^/]+)/);
            return match ? match[1] : url;
        }

        if (platform === 'soundcloud') {
            const match = pathname.match(/^\/([^/]+)/);
            return match ? match[1] : url;
        }

        if (platform === 'spotify') {
            const match = pathname.match(/^\/artist\/([^/]+)/);
            if (match) return match[1];
            const userMatch = pathname.match(/^\/user\/([^/]+)/);
            if (userMatch) return userMatch[1];
            return pathname.replace(/^\//, '').replace(/\/$/, '');
        }

        if (platform === 'twitter') {
            const match = pathname.match(/^\/([^/]+)/);
            return match ? `@${match[1]}` : url;
        }

        if (platform === 'custom') {
            return url.replace(/^https?:\/\//, '');
        }

        return url;
    } catch {
        return url;
    }
}

export function buildSocialUrl(input: string, platform: PlatformType): string {
    if (!input.trim()) return '';
    const trimmed = input.trim();

    try {
        new URL(trimmed);
        return trimmed;
    } catch {
        if (platform === 'custom') {
            if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
                return trimmed;
            }
            try {
                new URL(`https://${trimmed}`);
                return `https://${trimmed}`;
            } catch {
                return trimmed;
            }
        }

        const username = trimmed.replace(/^@/, '');

        if (platform === 'instagram') {
            return `https://instagram.com/${username}`;
        }
        if (platform === 'bandcamp') {
            return `https://${username}.bandcamp.com`;
        }
        if (platform === 'facebook') {
            return `https://facebook.com/${username}`;
        }
        if (platform === 'soundcloud') {
            return `https://soundcloud.com/${username}`;
        }
        if (platform === 'spotify') {
            return `https://open.spotify.com/artist/${username}`;
        }
        if (platform === 'twitter') {
            return `https://twitter.com/${username}`;
        }

        return trimmed;
    }
}

export function validateSocialUrl(url: string): boolean {
    if (!url.trim()) return false;

    try {
        new URL(url);
        return true;
    } catch {
        return false;
    }
}

const BandcampIcon = () => {
    return React.createElement(
        'svg',
        { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'currentColor' },
        React.createElement('path', { d: 'M0 18.75l7.437-13.5h16.563l-7.438 13.5z' })
    );
};

export function getPlatformIcon(platform: PlatformType): React.ComponentType<{size?: number}> {
    switch (platform) {
        case 'instagram':
            return IconBrandInstagram;
        case 'facebook':
            return IconBrandFacebook;
        case 'soundcloud':
            return IconBrandSoundcloud;
        case 'spotify':
            return IconBrandSpotify;
        case 'twitter':
            return IconBrandTwitter;
        case 'bandcamp':
            return BandcampIcon;
        case 'custom':
            return IconLink;
        default:
            return IconLink;
    }
}

export function getPlatformDisplayName(platform: PlatformType): string {
    switch (platform) {
        case 'instagram':
            return 'Instagram';
        case 'bandcamp':
            return 'Bandcamp';
        case 'facebook':
            return 'Facebook';
        case 'soundcloud':
            return 'SoundCloud';
        case 'spotify':
            return 'Spotify';
        case 'twitter':
            return 'Twitter';
        case 'custom':
            return 'Custom Link';
        default:
            return platform;
    }
}

export function generateSocialLinkId(): string {
    return `social-link-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}
